import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  UserSearch,
  RefreshCw,
  Loader2,
  Pencil,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const STATI = [
  "inviata",
  "vista",
  "lavorazione",
  "cv_inviato",
  "completata",
  "annullata",
];

function statoBadge(stato) {
  const s = (stato || "").toLowerCase();
  if (s === "completata") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "cv_inviato" || s === "lavorazione") return "bg-violet-50 text-violet-700 border-violet-200";
  if (s === "annullata") return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
}

export default function RichiestePersonaleAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({
    stato: "inviata",
    note_interne: "",
    cv_inviati: "",
    data_completamento: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroStato && filtroStato !== "tutti") params.set("stato", filtroStato);
      const res = await fetch(
        `${API}/api/admin/recruiting-requests?${params.toString()}`,
        { headers }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [filtroStato]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      stato: row.stato || "inviata",
      note_interne: row.note_interne || "",
      cv_inviati:
        typeof row.cv_inviati === "number"
          ? String(row.cv_inviati)
          : Array.isArray(row.cv_ricevuti)
            ? String(row.cv_ricevuti.length)
            : row.cv_inviati != null
              ? String(row.cv_inviati)
              : "",
      data_completamento: (row.data_completamento || "").slice(0, 10),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const body = {
        stato: form.stato,
        note_interne: form.note_interne,
        cv_inviati: form.cv_inviati === "" ? null : Number(form.cv_inviati),
        data_completamento: form.data_completamento || null,
      };
      const id = editRow.id || editRow._id;
      const res = await fetch(`${API}/api/admin/recruiting-requests/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      toast.success("Richiesta aggiornata");
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <UserSearch className="w-5 h-5 text-violet-600" />
            Richieste personale
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Gestione stati delle richieste Trova Personale inviate dalle strutture
            (ex Richieste_Personale Zoho).
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filtroStato} onValueChange={setFiltroStato}>
            <SelectTrigger className="w-40 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              {STATI.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="p-3">Ruolo</th>
              <th className="p-3">Struttura</th>
              <th className="p-3">Urgenza</th>
              <th className="p-3">Stato</th>
              <th className="p-3">CV</th>
              <th className="p-3">Creata</th>
              <th className="p-3 w-12" />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id || row._id}
                className="border-t border-slate-100 hover:bg-slate-50/80"
              >
                <td className="p-3">
                  <p className="font-medium">{row.ruolo || "—"}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">
                    {row.descrizione || row.user_email || ""}
                  </p>
                </td>
                <td className="p-3 text-slate-600">{row.struttura_nome || "—"}</td>
                <td className="p-3 text-xs">{row.urgenza || "—"}</td>
                <td className="p-3">
                  <Badge variant="outline" className={`text-[10px] ${statoBadge(row.stato)}`}>
                    {row.stato || "—"}
                  </Badge>
                </td>
                <td className="p-3 tabular-nums">
                  {row.cv_inviati != null
                    ? row.cv_inviati
                    : Array.isArray(row.cv_ricevuti)
                      ? row.cv_ricevuti.length
                      : "—"}
                </td>
                <td className="p-3 text-xs text-slate-500">
                  {(row.created_at || "").slice(0, 10) || "—"}
                </td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="p-10 text-center text-slate-500">
                  Nessuna richiesta personale
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiorna richiesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm((p) => ({ ...p, stato: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATI.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>CV inviati (numero)</Label>
              <Input
                type="number"
                min="0"
                value={form.cv_inviati}
                onChange={(e) => setForm((p) => ({ ...p, cv_inviati: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data completamento</Label>
              <Input
                type="date"
                value={form.data_completamento}
                onChange={(e) =>
                  setForm((p) => ({ ...p, data_completamento: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Note interne</Label>
              <Input
                value={form.note_interne}
                onChange={(e) => setForm((p) => ({ ...p, note_interne: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
